import connectionService from '@services/internal/connection.service';
import moment from 'moment';
import { useSelector } from 'react-redux';
import 'strophe.js';
import TStore from 'types/store';
import DB from '../lib/db';
import appStore from '../store';
import { actions as directChatActions } from '../store/ducks/direct-chat';
import { actions as roomsActions } from '../store/ducks/rooms';
import { actions as userActions } from '../store/ducks/users';
import { actions as xmppActions } from '../store/ducks/xmpp';
import { TAttachment } from '../types/attachments';
import { TChatContact, TDirectMessage, TPresence } from '../types/direct-chat';
import { TRoom } from '../types/rooms';
import { TCurrentUser } from '../types/users';

window.DOMParser = require('xmldom').DOMParser;

const parser = new DOMParser();
export const NS_MUC = 'http://jabber.org/protocol/muc';

let onPresenceCbHandler: Strophe.Handler,
  onPublicMessageCbHandler: Strophe.Handler,
  onDirectMessageHandler: Strophe.Handler,
  presenceHandler: Strophe.Handler;

export const connectCb = async (status: Strophe.Status, connection: any) => {
  if (status === Strophe.Status.CONNECTING) {
    appStore.dispatch(xmppActions.setStatus('CONNECTING'));
  }
  if (
    status === Strophe.Status.AUTHFAIL ||
    status === Strophe.Status.ERROR ||
    status === Strophe.Status.DISCONNECTED
  ) {
    appStore.dispatch(xmppActions.setStatus('FAILED'));
  }
  if (status === Strophe.Status.CONNECTED || status === Strophe.Status.ATTACHED) {
    if (onPresenceCbHandler) {
      connection.deleteHandler(onPresenceCbHandler);
    }
    if (onPublicMessageCbHandler) {
      connection.deleteHandler(onPublicMessageCbHandler);
    }

    onPresenceCbHandler = connection.addHandler(onPresenceCb, null, 'presence');
    onPublicMessageCbHandler = connection.addHandler(
      onPublicMessageCb,
      null,
      'message',
      'groupchat',
    );

    // enable UNREAD messages
    enableUnreads(connection);

    await retriveArchivesMessages(() => {
      getContacts(() => {
        appStore.dispatch(xmppActions.setStatus('CONNECTED'));
        appStore.dispatch(directChatActions.getUnreadsRequest());
      });
    });
  }
};

const retriveArchivesMessages = async (callback: any) => {
  const connection = await connectionService.waitForConnection();
  const jabberId = appStore.getState().users.currentUser?.jabberId as string;

  const iqArchived = $iq({
    type: 'set',
    id: jabberId,
  })
    .c('prefs', { xmlns: 'urn:xmpp:mam:2', default: 'always' })
    .c('always')
    .c('never');

  connection.sendIQ(iqArchived, (iqs: any) => {
    callback();
  });
};

export const parsePresence = (presenceString: string): object => {
  const result: any = {};
  const domXML = parser.parseFromString(presenceString, 'application/xml');
  const presence = domXML.getElementsByTagName('presence')[0];

  // extract values
  result.from = presence.getAttribute('from');
  result.type = presence.getAttribute('type');

  const nickname = Strophe.getResourceFromJid(result.from);
  const items = Array.from(presence.getElementsByTagName('item'));

  for (const item of items) {
    result.participant = {
      nickname,
      jid: item.getAttribute('jid'),
    };
  }

  const statuses = Array.from(presence.getElementsByTagName('status'));

  for (const status of statuses) {
    const code = status.getAttribute('code');
    if (code === '110') {
      result.selfJoin = true;
    }
    if (code === '210') {
      result.nicknameChange = true;
    }
  }
  return result;
};

const onPresenceCb = (presence: any) => {
  // parse presence object
  const serializedPresence = Strophe.serialize(presence);
  const presenceObject: any = parsePresence(serializedPresence);

  // check if we are in proper room
  const currentRoom = appStore.getState().rooms.currentRoom;
  const presenceRoom = Strophe.getBareJidFromJid(presenceObject.from);

  if (currentRoom && currentRoom === presenceRoom) {
    const nickname = Strophe.getResourceFromJid(presenceObject.from);

    // direct presence?
    if (nickname === null) {
      return true;
    }

    if (presenceObject.type === 'error') {
      // error joining room, reset app
      // connection.disconnect();
      return true;
    }

    // add user to participants list
    if (presenceObject.type !== 'unavailable') {
      appStore.dispatch(roomsActions.setParticipants(presenceObject.participant));
    }

    // remove another user from participants list
    if (presenceObject.type === 'unavailable') {
      appStore.dispatch(roomsActions.removeParticipant(presenceObject.participant));
    }
  }
};

const onPublicMessageCb = (message: any) => {
  const myNickname = appStore.getState().users.currentUser?.jabberId;
  const result: any = parseMessage(Strophe.serialize(message));
  const currentRoom = appStore.getState().rooms.currentRoom;
  const presenceRoom = result.room;
  const providerId = useSelector((state: TStore) => state.users.business[0].id);

  // make sure message is from the right place
  if (currentRoom === presenceRoom) {
    if (result.subject) {
    }

    // is message from a user or the room itself?
    if (!result.nick) {
      return true;
    }

    if (result.body || result.attachment) {
      if (result.to.indexOf('guest@ejabberd.01people.tech') !== -1) {
        return true;
      }
      appStore.dispatch(
        roomsActions.addMessageRequest({
          id: result.id,
          sender: result.nick === myNickname ? 'me' : 'other',
          from: result.nick,
          text: result.body,
          stamp: result.stamp,
          attachment: result.attachment,
        }),
      );
      appStore.dispatch(userActions.postSearchRequest({ providerId, jabberIds: [result.nick] }));
    }
  }
  return true;
};

const parseMessage = (messageString: string): object => {
  const result: any = {};
  const domXML = parser.parseFromString(messageString, 'application/xml');
  const message = domXML.getElementsByTagName('message')[0];
  result.id = message.getAttribute('id');
  result.from = message.getAttribute('from');
  result.to = message.getAttribute('to');
  result.room = Strophe.getBareJidFromJid(result.from);
  result.nick = Strophe.getResourceFromJid(result.from);

  const body = message.getElementsByTagName('body');

  if (body.length > 0) {
    result.body = body[0].textContent;
  }

  const delayed = message.getElementsByTagName('delay');
  if (delayed.length > 0) {
    result.delay = true;
    result.stamp = delayed[0].getAttribute('stamp');
  }

  const subject = message.getElementsByTagName('subject');
  if (subject.length > 0) {
    result.subject = subject[0].textContent;
  }

  const attachments = message.getElementsByTagName('attachment');
  if (attachments.length > 0) {
    const id = attachments[0].getAttribute('id');
    const type = attachments[0].getAttribute('type');
    const size = attachments[0].getAttribute('size');
    const name = attachments[0].getAttribute('name');
    result.attachment = {
      id,
      type,
      size,
      name,
    };
  }
  result.stamp = result.stamp || moment().format();
  return result;
};

export const sendRoomMessage = async (message: string) => {
  const connection = await connectionService.waitForConnection();
  const currentRoom = appStore.getState().rooms.currentRoom as string;
  const rooms: TRoom[] = appStore.getState().rooms.rooms;
  const id = connection.getUniqueId();
  const { profile }: TCurrentUser = appStore.getState().users.currentUser!;

  const room = rooms.find((r) => r.id === currentRoom);
  const roomName = room?.name || '';

  connection.send(
    $msg({
      id,
      to: currentRoom,
      type: 'groupchat',
    })
      .c('body')
      .t(message)
      .up()
      .c('x', { xmlns: 'p1:push:custom', key: 'category', value: 'GROUPCHAT' })
      .up()
      .c('x', {
        xmlns: 'p1:push:custom',
        key: 'aps.alert.title',
        value: `${roomName} - ${profile.firstName} ${profile.lastName}`,
      })
      .up()
      .c('x', { xmlns: 'p1:push:custom', key: 'messageId', value: id })
      .up()
      .c('x', {
        xmlns: 'p1:push:custom',
        key: 'body',
        value: message,
      }),
  );
};

export const sendRoomAttachment = async (message: string, attachment: TAttachment) => {
  const connection = await connectionService.waitForConnection();

  const currentRoom = appStore.getState().rooms.currentRoom as string;
  const rooms: TRoom[] = appStore.getState().rooms.rooms;
  const id = connection.getUniqueId();
  const { profile }: TCurrentUser = appStore.getState().users.currentUser!;

  const room = rooms.find((r) => r.id === currentRoom);
  const roomName = room?.name || '';

  const msg = $msg({
    to: currentRoom,
    type: 'groupchat',
    id: id,
  })
    .c('body')
    .t(message)
    .up()
    .c('attachment', {
      type: attachment.type as string,
      id: attachment.id as string,
      size: attachment.size as string,
      name: attachment.name as string,
    })
    .up()
    .c('x', { xmlns: 'p1:push:custom', key: 'category', value: 'GROUPCHAT' })
    .up()
    .c('x', {
      xmlns: 'p1:push:custom',
      key: 'aps.alert.title',
      value: `${roomName} - ${profile.firstName} ${profile.lastName}`,
    })
    .up()
    .c('x', { xmlns: 'p1:push:custom', key: 'messageId', value: id })
    .up()
    .c('x', {
      xmlns: 'p1:push:custom',
      key: 'body',
      value: 'attachment',
    });

  connection.send(msg);
};

/**
 * parse direct message
 * @param {string} message
 * @returns {object} from, to, body
 */
export const parseDirectMessage = (text: string, isArchived: boolean): TDirectMessage => {
  const result: TDirectMessage = {
    from: '',
    to: '',
    body: '',
    uuid: '',
    thread: '',
    stamp: '',
    alertTitle: '',
    alertVital: undefined,
    alertStyle: '',
  };
  const myNickname = appStore.getState().users.currentUser!.jabberId;

  let alertTitle: string = '';
  let alertVital: any = '';
  let alertStyle: string = '';

  let message: any = null;
  let stamp: any = null;

  const domXML = parser.parseFromString(text, 'application/xml');
  const mainMessage = domXML.getElementsByTagName('message')[0];

  if (isArchived) {
    const forwarded = mainMessage.getElementsByTagName('forwarded')[0];
    message = forwarded.getElementsByTagName('message')[0];
  } else {
    message = mainMessage;
  }
  const from = message.getAttribute('from');
  const to = message.getAttribute('to');
  const body = message.getElementsByTagName('body');
  const uuid = message.getAttribute('id');
  const archived = message.getElementsByTagName('archived')[0];
  const archivedId = archived.getAttribute('id');

  const delay = domXML.getElementsByTagName('delay')[0];
  if (delay) {
    stamp = delay.getAttribute('stamp');
  }
  const fromJid = from ? from.split('/')[0] : null;
  const toJid = to ? to.split('/')[0] : null;

  const attachments = message.getElementsByTagName('attachment');

  if (attachments.length > 0) {
    const id = attachments[0].getAttribute('id');
    const type = attachments[0].getAttribute('type');
    const size = attachments[0].getAttribute('size');
    const name = attachments[0].getAttribute('name');
    result.attachment = {
      id,
      type,
      size,
      name,
    };
  }

  try {
    const custom = domXML.getElementsByTagName('x');
    for (let i = 0; i < custom.length; i++) {
      if (custom[i].getAttribute('key') === 'aps.alert.title') {
        alertTitle = custom[i].getAttribute('value') || '';
      }
    }
  } catch {}

  try {
    const custom = domXML.getElementsByTagName('x');
    for (let i = 0; i < custom.length; i++) {
      if (custom[i].getAttribute('key') === 'aps.alert.vital') {
        alertVital = custom[i].getAttribute('value') || '';
      }
    }
  } catch {}

  try {
    const custom = domXML.getElementsByTagName('x');
    for (let i = 0; i < custom.length; i++) {
      if (custom[i].getAttribute('key') === 'aps.alert.style') {
        alertStyle = custom[i].getAttribute('value') || '';
      }
    }
  } catch {}

  result.from = fromJid ? fromJid : '';
  result.to = toJid ? toJid : '';
  result.uuid = uuid ? uuid : '';
  result.thread = myNickname === result.from ? result.to : result.from;
  result.stamp = stamp ? stamp : '';
  result.alertTitle = alertTitle;
  result.alertVital = alertVital;
  result.alertStyle = alertStyle;
  result.archivedId = archivedId;

  if (body.length > 0) {
    result.body = body[0].textContent ? body[0].textContent : '';
  }
  return result;
};

/**
 * On direct message handler. One-to-one chat.
 * @param {XML} message
 */
const onDirectMessage = (message: Element) => {
  const result = parseDirectMessage(Strophe.serialize(message), false);
  if (result.body || result.attachment) {
    const from = result.from.split('/')[0];
    const message: TDirectMessage = {
      from,
      to: result.to.split('/')[0],
      body: result.body,
      uuid: result.uuid,
      thread: from,
      attachment: result.attachment,
      stamp: moment().format(),
      alertTitle: result.alertTitle,
      alertVital: result.alertVital,
      alertStyle: result.alertStyle,
    };

    DB.addDirectMessage(message);
    appStore.dispatch(directChatActions.addDirectMessage(message));
  }
  return true;
};

/**
 * parse roaster
 *
 * @param {string} text
 * @returns {array} of contacts
 */
export const parseContacts = (text: string) => {
  const domXML = parser.parseFromString(text, 'application/xml');
  const items = domXML.getElementsByTagName('item');
  const result: TChatContact[] = [];

  // tslint:disable-next-line
  for (let i = 0; i < items.length; i += 1) {
    result.push({
      jid: items[i].getAttribute('jid') || '',
      name: items[i].getAttribute('name') || items[i].getAttribute('jid'),
      subscription: items[i].getAttribute('subscription'),
      status: null,
    });
  }
  return result;
};

/**
 * parse contact presence
 * @param {string} text
 * @returns {object} jid and presense
 * @author ihor.zhuk
 */
export const parseContactPresence = (text: string) => {
  let result: TPresence | null = null;
  const domXML = parser.parseFromString(text, 'application/xml');
  const presence = domXML.getElementsByTagName('presence')[0];
  const from = presence.getAttribute('from');
  const to = presence.getAttribute('to');
  const type = presence.getAttribute('type');
  const fromJid = from ? from.split('/')[0] : null;
  const toJid = to ? to.split('/')[0] : null;

  result = {
    jid: fromJid,
    status: type,
    to: toJid,
  };
  return result;
};

/**
 * get xmpp contacts and start listen to incoming messages.
 * Approve incoming subscriptions
 */
export const getContacts = async (callback: any) => {
  const connection = await connectionService.waitForConnection();
  const iq = $iq({ type: 'get' }).c('query', { xmlns: 'jabber:iq:roster' });

  if (onDirectMessageHandler) {
    connection.deleteHandler(onDirectMessageHandler);
  }
  onDirectMessageHandler = connection.addHandler(
    onDirectMessage,
    // @ts-expect-error wrong types
    null,
    'message',
    'chat',
  );

  connection.sendIQ(iq, (iqs: any) => {
    const contacts = parseContacts(Strophe.serialize(iqs));
    appStore.dispatch(directChatActions.setContacts(contacts));

    if (presenceHandler) {
      connection.deleteHandler(presenceHandler);
    }

    presenceHandler = connection.addHandler(
      (presence: any) => {
        const presenceFormatted = parseContactPresence(Strophe.serialize(presence));
        if (presenceFormatted) {
          appStore.dispatch(directChatActions.setPresence(presenceFormatted));

          // if there is incoming request
          if (presenceFormatted.status === 'subscribe') {
            // automatically approve subscription
            connection.send($pres({ to: presenceFormatted.jid as string, type: 'subscribed' }));
            connection.send($pres({ to: presenceFormatted.jid as string, type: 'subscribe' }));
          }
        }
        return true;
      },
      // @ts-expect-error wrong types
      null,
      'presence',
    );

    connection.send(
      // @ts-ignore
      $pres().c('priority').t(1),
    );

    callback();
  });
};

export const sendDirectMessage = async (message: string, jid: string, messageId: string) => {
  const handler = await connectionService.waitForConnection();
  const { profile }: TCurrentUser = appStore.getState().users.currentUser!;

  handler.send(
    $msg({
      to: jid,
      type: 'chat',
      id: messageId,
    })
      .c('body')
      .t(message)
      .up()
      .c('x', { xmlns: 'p1:push:custom', key: 'category', value: 'MESSAGE' })
      .up()
      .c('x', {
        xmlns: 'p1:push:custom',
        key: 'aps.alert.title',
        value: `${profile.firstName} ${profile.lastName}`,
      })
      .up()
      .c('x', { xmlns: 'p1:push:custom', key: 'messageId', value: messageId })
      .up()
      .c('x', { xmlns: 'p1:push:custom', key: 'body', value: message }),
  );
};

export const sendDirectAttachment = async (
  message: string,
  attachment: TAttachment,
  jid: string,
  messageId: string,
) => {
  const handler = await connectionService.waitForConnection();
  const { profile }: TCurrentUser = appStore.getState().users.currentUser!;

  const msg = $msg({
    to: jid,
    type: 'chat',
    id: messageId,
  })
    .c('body')
    .t(message || 'attachment')
    .up()
    .c('attachment', {
      type: attachment.type as string,
      id: attachment.id as string,
      size: attachment.size as string,
      name: attachment.name as string,
    })
    .up()
    .c('x', { xmlns: 'p1:push:custom', key: 'category', value: 'MESSAGE' })
    .up()
    .c('x', {
      xmlns: 'p1:push:custom',
      key: 'aps.alert.title',
      value: `${profile.firstName} ${profile.lastName}`,
    })
    .up()
    .c('x', { xmlns: 'p1:push:custom', key: 'messageId', value: messageId })
    .up()
    .c('x', { xmlns: 'p1:push:custom', key: 'body', value: 'attachment' });

  handler.send(msg);
};

export const addContact = async (jid: string, name: string) => {
  const connection = await connectionService.waitForConnection();
  const data = {
    jid,
    name,
  };
  const iq = $iq({ type: 'set' }).c('query', { xmlns: 'jabber:iq:roaster' }).c('item', data);
  connection.sendIQ(iq);
  const subscribe = $pres({ to: data.jid, type: 'subscribe' });
  connection.send(subscribe);
};

/**
 * TODO: currently timeout
 * @param connection
 * @param jabberId
 * @param homeView
 */
export const getMessagesFromLastMonth = async (
  connection: any,
  jabberId: string,
  homeView: boolean = false,
) => {
  // resolve time attrs by "homeView" property.
  // on dashboard this should be one month ago messages
  // on communication view, this should be all the messages that someone wrote to doctor
  // we cuppose communication view by default

  const timeAttrs: any = {
    start: moment().subtract(24, 'months').format('YYYY-MM-DDTHH:mm:ss') + 'Z',
  };

  if (homeView) {
    timeAttrs.start = moment().subtract(1, 'months').format('YYYY-MM-DDTHH:mm:ss') + 'Z';
  }

  // FIXME: XMPP si slower as 5000, so this always reject
  // const timeoutPromise = new Promise((resolve, reject) => {
  //   const wait = setTimeout(() => {
  //     clearTimeout(wait);
  //     reject('fail');
  //   }, 5000); // Timeout
  // });

  const mamPromise = new Promise((res, rej) => {
    const messages: TDirectMessage[] = [];
    connection.mam.query(jabberId, {
      ...timeAttrs,
      before: '',
      max: 500,
      onMessage: (message: any) => {
        const result = parseDirectMessage(Strophe.serialize(message), true);
        messages.push(result);
        return true;
      },
      onComplete: (response: string) => {
        res(messages);
      },
      onError: (err: any) => {
        rej(err);
      },
    });
  });

  return mamPromise;
};

/**
 * Sends IQ Stanza to enable unread messages
 *
 * @param connection
 * @author jozef.repan
 */
export const enableUnreads = (
  connection: any,
  // TODO: MEDIUM implement rej
) =>
  new Promise((res, rej) => {
    const stanza: any = $iq({
      id: 'iq:1',
      type: 'set',
    }).c('enable', { xmlns: 'p1:unread' });

    connection.sendIQ(stanza, {
      onMessage: function (message: any) {
        const result = parseDirectMessage(Strophe.serialize(message), true);
        console.info('Enable unread result:', result);
        return true;
      },
      onComplete: function (response: string) {
        res(response);
      },
    });
  });

export const getUnread = (connection: any) =>
  new Promise((resolve, reject) => {
    const stanza: any = $iq({
      type: 'get',
    }).c('status', { xmlns: 'p1:unread' });

    connection.sendIQ(stanza, (response: any) => {
      resolve(response);
      return true;
    });
  });

export const getContactMessages = (
  connection: any,
  jabberId: string,
  contactJid: string,
  config?: any,
): Promise<any> => {
  return new Promise((res, rej) => {
    const messages: TDirectMessage[] = [];

    connection.mam.query(jabberId, {
      with: contactJid,
      onMessage: (message: any) => {
        const result = parseDirectMessage(Strophe.serialize(message), true);
        messages.push(result);
        return true;
      },
      onComplete: (response: string) => {
        res(messages);
      },
      ...config,
    });
  });
};

export function parseSubscribedRooms(text: string) {
  const domXML = parser.parseFromString(text, 'application/xml');
  const subscriptionsElement = domXML.getElementsByTagName('subscriptions')[0];

  const subscriptions = [];
  const subscriptionArray = subscriptionsElement.getElementsByTagName('subscription');
  for (let index = 0; index < subscriptionArray.length; index++) {
    const subscription = subscriptionArray[index];
    const jid = subscription.getAttribute('jid');
    subscriptions.push(jid);
  }

  return subscriptions;
}
